import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Send, Sparkles, Loader2 } from 'lucide-react';
import { api, apiStream } from '@/shared/api';
import MarkdownView from '@/components/MarkdownView';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface AIHealth {
  ok: boolean;
  provider: string;
  model: string;
}

interface AIConfig {
  llm_provider: string;
  llm_model: string;
  embedding_provider: string;
  embedding_model: string;
}

export default function AIPlaygroundPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const { data: health } = useQuery<AIHealth>({
    queryKey: ['ai-health'],
    queryFn: () => api('/api/v1/ai/health'),
  });
  const { data: config } = useQuery<AIConfig>({
    queryKey: ['ai-config'],
    queryFn: () => api('/api/v1/ai/config'),
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const userMsg: Message = { role: 'user', content: input };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setStreaming(true);

    // Append empty assistant message
    setMessages([...newMessages, { role: 'assistant', content: '' }]);

    try {
      let acc = '';
      await apiStream(
        '/api/v1/ai/chat/stream',
        {
          messages: newMessages,
          temperature: 0.2,
          max_tokens: 1024,
        },
        (token) => {
          acc += token;
          setMessages([...newMessages, { role: 'assistant', content: acc }]);
        },
      );
    } catch (e) {
      setMessages([
        ...newMessages,
        { role: 'assistant', content: `[ERROR] ${String(e)}` },
      ]);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" style={{ height: 'calc(100vh - 240px)', display: 'grid', gridTemplateRows: 'auto auto 1fr' }}>
      {/* HEADER */}
      <div className="mb-3 pb-3 border-b-2 border-ink">
        <div className="label-mono mb-2 flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-brass" />
          AI Playground · Smoke Test
        </div>
        <h1 className="font-display font-bold text-[32px] leading-none tracking-tighter">
          Tanya Asisten AI
        </h1>
        <div className="font-mono text-[11px] text-ink-2 mt-3 flex gap-4 flex-wrap">
          <span>Provider: <strong className="text-brass">{config?.llm_provider || '—'}</strong></span>
          <span>Model: <strong className="text-ink">{config?.llm_model || '—'}</strong></span>
          <span>Status: <strong className={health?.ok ? 'text-olive' : 'text-crimson'}>{health?.ok ? '● ONLINE' : '● OFFLINE'}</strong></span>
        </div>
      </div>

      {!health?.ok ? (
        <div className="mb-3 p-3 border-2 border-crimson bg-crimson/5 font-mono text-[11px] text-crimson">
          ⚠ LLM endpoint tidak bisa dijangkau. Pastikan Ollama jalan di host port 11434.
        </div>
      ) : <div />}

      {/* CHAT PANEL: scrollable middle + sticky form */}
      <div className="grid min-h-0 border border-ink-line bg-paper-2/20" style={{ gridTemplateRows: '1fr auto' }}>
        {/* MESSAGES (WhatsApp-style) */}
        <div className="overflow-y-auto px-4 py-4 space-y-2 min-h-0">
          {messages.length === 0 && (
            <div className="text-center py-12 font-mono text-xs text-ink-3">
              <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-40" />
              Belum ada percakapan. Ketik pertanyaan di bawah untuk mulai.
            </div>
          )}
          {messages.map((m, i) => {
            const isUser = m.role === 'user';
            return (
              <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[78%] px-3.5 py-2 shadow-sm ${
                    isUser
                      ? 'bg-bluestamp text-paper rounded-tl-lg rounded-tr-lg rounded-bl-lg'
                      : 'bg-paper border border-ink-line rounded-tl-lg rounded-tr-lg rounded-br-lg'
                  }`}
                >
                  <div className={`font-mono text-[9px] uppercase tracking-widest font-bold mb-1 ${isUser ? 'text-paper/70' : 'text-brass'}`}>
                    {isUser ? 'Penyidik' : `Asisten · ${config?.llm_model || ''}`}
                  </div>
                  {isUser ? (
                    <div className="font-serif text-[14px] leading-snug whitespace-pre-wrap text-paper">{m.content}</div>
                  ) : (
                    <div className="text-[13px] leading-snug">
                      <MarkdownView>{m.content}</MarkdownView>
                      {streaming && i === messages.length - 1 && (
                        <span className="inline-block w-2 h-3.5 bg-maroon align-text-bottom ml-0.5 animate-pulse" />
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        {/* FORM sticky bottom */}
        <div className="px-3 py-3 border-t border-ink-line bg-paper-2/40 backdrop-blur-sm flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Tulis pertanyaan... (Enter kirim · Shift+Enter newline)"
            rows={2}
            className="flex-1 bg-paper border border-ink-line px-3 py-2 font-serif text-[14px] focus:outline-none focus:border-brass resize-none"
            disabled={streaming}
          />
          <button
            onClick={send}
            disabled={streaming || !input.trim()}
            className="self-stretch px-4 font-mono text-[11px] uppercase tracking-wider font-semibold bg-ink text-paper border border-ink hover:bg-brass transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {streaming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Kirim
          </button>
        </div>
      </div>
    </div>
  );
}
